'use client';

import { forwardRef } from 'react';
import { clsx } from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showRequired?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, leftIcon, rightIcon, id, showRequired, required, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s/g, '-');
    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-semibold text-[#111827] mb-2"
          >
            {label}
            {(showRequired || required) && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-[#5F6F52]/70">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              'w-full rounded-xl bg-[#F9F7F0] border border-[#5F6F52]/40 px-4 py-3 text-sm text-[#2D3536] placeholder-[#9CA3AF] hover:border-[#5F6F52] focus:outline-none focus:ring-2 focus:ring-[#5F6F52]/20 focus:border-[#5F6F52] transition-all shadow-xs',
              '[&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_#F9F7F0] [&:-webkit-autofill]:-webkit-text-fill-color-[#2D3536]',
              leftIcon && '!pl-12',
              rightIcon && '!pr-12',
              error && 'bg-red-50 focus:ring-red-500/10 border-red-300',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-4 top-1/2 z-10 -translate-y-1/2 text-[#5F6F52]/70">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-xs font-medium text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
